import { Injectable } from '@angular/core';
import {
  AuthActionTypes,
  AuthenticatedFailure,
  AuthenticatedSuccess,
  FetchAuthenticatedUser,
  Login,
  LoginFailure,
  LoginRedirect,
  LoginSuccess,
  RemoveToken
} from '@app/auth/actions/auth';
import { HideAllAlerts } from '@app/core/actions/alert';
import { ChangeLanguage } from '@app/core/actions/translations';
import { AuthService } from '@app/core/services/auth.service';
import { DictionaryService } from '@app/core/services/dictionary.service';
import { RedirectService } from '@app/core/services/redirect.service';
import { getLanguageId } from '@app/core/utils';
import * as fromRoot from '@app/reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { isNotEmpty, mapPayload } from '@util';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AuthEffects {
  alerts$ = this.store.pipe(select(fromRoot.getAlerts));

  login$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.Login),
    map((action: Login) => action.payload),
    exhaustMap(credentials =>
      this.authService
        .login(credentials)
        .pipe(
          tap(({token}) => this.authService.saveToken(token)),
          switchMap(() => [new FetchAuthenticatedUser()]),
          catchError(error => of(new LoginFailure(error)))
        )
    )
  ));

  authenticated$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.Authenticated),
    exhaustMap(() =>
      this.authService
        .authenticatedUser()
        .pipe(
          switchMap(user => of(new AuthenticatedSuccess(user))),
          catchError(error => of(new AuthenticatedFailure(error)))
        )
    )
  ));

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.Logout),
    switchMap(() => {
      this.dictionaryService.resetState();
      return [new RemoveToken(), new LoginRedirect()];
    })
  ));

  loginSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.LoginSuccess),
    tap(() => this.redirectService.goToHome())
  ), {dispatch: false});

  loginRedirect$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AuthenticatedFailure, AuthActionTypes.LoginRedirect),
    tap(() => this.redirectService.goToLogin())
  ), {dispatch: false});

  removeToken$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.RemoveToken),
    tap(() => this.authService.removeToken()
    )
  ), {dispatch: false});

  fetchAuthenticatedUser$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.FetchAuthenticatedUser),
    exhaustMap(() =>
      this.authService
        .authenticatedUser()
        .pipe(
          switchMap(user => [new AuthenticatedSuccess(user), new LoginSuccess(user)]),
          catchError(error => of(new AuthenticatedFailure(error)))
        )
    )
  ));

  authenticatedUserSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AuthenticatedSuccess),
    mapPayload(),
    exhaustMap((user) => of(new ChangeLanguage(getLanguageId(user))))
  ));

  changeCredentials$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.ChangeCredentials),
    withLatestFrom(this.alerts$),
    exhaustMap(([_, alerts]) =>
      isNotEmpty(alerts)
        ? [new HideAllAlerts()]
        : of()
    )
  ));

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private redirectService: RedirectService,
    private store: Store<any>,
    private dictionaryService: DictionaryService
  ) {
  }
}
